import React, { useCallback, useState, useRef, useEffect, useMemo, forwardRef, useContext } from 'react';
import { Alert, Pressable, Text, View, Image, Platform, Linking, KeyboardAvoidingView, Keyboard, useWindowDimensions } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import TempScreen from '@components/Global/htmlEditor/HtmlEditor';
import Button from '@components/UI/Button/Button';
import * as Animatable from 'react-native-animatable';
import { MaterialCommunityIcons, Fontisto } from '@expo/vector-icons';
import { AntDesign, Entypo } from '@expo/vector-icons';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import UploadHtml from '@components/Global/UploadHtml';
import BottomSheet from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheet/BottomSheet';
import { store } from '@reduxLocal/persistState';
import { HIDE_TOOLBAR, SHOW_TOOLBAR } from '@reduxLocal/actions/types.js';
import { useNavigation, useRoute } from '@react-navigation/native';
import RobotHelper from '@components/RobotHelper/RobotHelper';
import { ToolbarContext } from '@hooks/toolbarContext';
import styles from '@stylesheet';
import FlipCard from 'react-native-flip-card';
import WebView from 'react-native-webview';
import Handlebars, { compile } from 'handlebars';
import AnimatedLottieView from '@utils/lottieAnimation';
import emailBody from '@utils/html/defaulKnowledgeHtml';
import apis from '@api/api';
import EditorToolbar from '@components/Global/EditorToolbar/EditorToolbar';
import { RichEditorProps } from 'react-native-pell-rich-editor/index';
import HelpChat from '@components/modals/HelpModal/Components/HelpChat';
import SwipeableRobot from '@components/RobotHelper/SwipeableRobot';
import { useDispatch, useSelector } from 'react-redux';
import { XStack, YStack } from 'tamagui';
import CustomDialog from '@components/UI/CustomDialog';
import { backgroundColor } from 'react-native-calendars/src/style';
import DomEditor, { DomEditorHandle } from './Components/DomEditor';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { DefaultRootState } from '@reduxLocal/persistState';

// Performance optimization utilities
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const throttle = (func: Function, limit: number) => {
  let inThrottle: boolean;
  return function executedFunction(...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

const CreateKnowledgeArticle = () => {
  const { params } = useRoute();
  const [showGlobalAssets, setShowGlobalAssets] = useState(false);
  const uploadHtmlRef = useRef<BottomSheet>(null);
  const navigation = useNavigation();
  const [canGoBack, setCanGoBack] = useState(false);
  // Create proper ref for DomEditor
  const editorRef = useRef<DomEditorHandle>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { ref, setRef, setEditorContext } = useContext(ToolbarContext);
  const [flip, setFlip] = useState(false);
  const [loading, setLoading] = useState(false);
  const panRef = useRef(null);
  const [hidingShowAssets, setHidingShowAssets] = useState(false);
  const [editorState, setEditorState] = useState(null);
  const [previewState, setPreviewState] = useState<string>();
  const [showSettings, setShowSettings] = useState(true);
  const webView = useRef<WebView>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [knowledgeContent, setKnowledgeContent] = useState<string>(''); // State to store fetched content
  const editorRefId = 'editorRefId';
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { height, width } = useWindowDimensions();

  // Performance optimization states
  const [cachedContent, setCachedContent] = useState<string>('');
  const [lastContentUpdate, setLastContentUpdate] = useState<number>(0);
  const contentCacheRef = useRef<string>('');
  const contentUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Theme-based colors for editor
  const editorColors = useMemo(() => ({
    background: theme ? '#ffffff' : '#1a1a1a',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
    border: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    text: theme ? '#333333' : '#e0e0e0',
    accent: theme ? '#7c3aed' : '#a78bfa',
    toolbar: theme ? '#ffffff' : '#252525',
    toolbarBorder: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
    shadow: theme 
      ? '0 2px 8px rgba(0, 0, 0, 0.1)' 
      : '0 2px 8px rgba(0, 0, 0, 0.3)',
  }), [theme]);

  const handleHideGlobalAssets = useCallback(() => { setShowGlobalAssets(false) }, [showGlobalAssets]);
  const handleShowUpload = useCallback(() => { 
    // Fix for BottomSheet methods
    if (uploadHtmlRef.current) {
      (uploadHtmlRef.current as any)?.present?.();
    }
  }, [uploadHtmlRef]);
  const handleToggleGlobalAssets = useCallback(() => { if (showGlobalAssets) { handleHideGlobalAssets() } else { setHidingShowAssets(false), setShowGlobalAssets(true) } }, [showGlobalAssets]);
  const _handleShowHelp = useCallback(() => { setShowHelp(!showHelp) }, [showHelp, navigation])
  const _handleEditorChange = useCallback(async (item) => { setEditorState(item); if (Platform.OS === 'web') { await webPreview() } }, [editorRef, previewState]);
  const _handleShowSettings = useCallback(() => { setShowSettings(!showSettings) }, [showSettings, navigation])
  const _handleUploadText = useCallback((item) => { if (editorRef.current) editorRef.current?.setContent(item) }, [editorRef]);

  const _handleGetContent = useCallback(async () => {
    try {
      const knowledgeArticleId = (params as any)?.knowledgeArticleId;
      if (!knowledgeArticleId) return;
      
      const res = await apis.getKnowledgeContent(knowledgeArticleId);
      
      if (res?.data?.content) {
        setKnowledgeContent(res.data.content);
      }
    } catch (error) {
      console.error('Failed to get knowledge content:', error);
    }
  }, [params]);

  useEffect(() => {
    // Fetch knowledge article content if ID is present
    const knowledgeArticleId = (params as any)?.knowledgeArticleId;
    if (knowledgeArticleId) {
      _handleGetContent();
    }

    const keyboardListener = Keyboard.addListener('keyboardDidShow', () => {
      setShowGlobalAssets(false);
      setShowSettings(false);
    });

    return () => {
      keyboardListener.remove();
    };
  }, [_handleGetContent, params]);

  // Optimized content getter with caching and throttling
  const getCachedContent = useCallback(() => {
    const now = Date.now();
    // Return cached content if less than 500ms old
    if (now - lastContentUpdate < 500 && contentCacheRef.current) {
      return contentCacheRef.current;
    }

    try {
      const content = editorRef.current?.getContent() || '';
      contentCacheRef.current = content;
      setLastContentUpdate(now);
      return content;
    } catch (e) {
      console.error('Get content error:', e);
      return contentCacheRef.current || '';
    }
  }, [lastContentUpdate]);

  // Debounced content update for AI context
  const debouncedContentUpdate = useCallback(
    debounce((content: string) => {
      setCachedContent(content);
      contentCacheRef.current = content;
      setLastContentUpdate(Date.now());
    }, 300),
    []
  );

  // Throttled content retrieval for performance
  const throttledGetContent = useCallback(
    throttle(() => {
      const content = getCachedContent();
      debouncedContentUpdate(content);
    }, 100),
    [getCachedContent, debouncedContentUpdate]
  );

  // Monitor editor availability and update context accordingly
  useEffect(() => {
    if (editorRef.current) {
      // Editor is available - context will be set by the main effect below
      return;
    } else {
      // Editor is not available - clear context to notify AI help
      setEditorContext(null);
    }
  }, [editorRef.current, setEditorContext]);

  // Register editor in context when available with performance optimizations
  useEffect(() => {
    if (!editorRef.current) return;

    // Create stable callback functions that don't change on every render
    const stableInsertContent = (content: string) => {
      try {
        // Get current content directly from editor to avoid dependency issues
        const current = editorRef.current?.getContent() || '';
        const sep = current.trim() ? '\n\n' : '';
        const newContent = current + sep + content;
        editorRef.current?.setContent(newContent);
        // Update cache immediately for responsive UI
        contentCacheRef.current = newContent;
        setLastContentUpdate(Date.now());
      } catch (e) {
        console.error('Insert content error:', e);
      }
    };

    const stableSetContent = (content: string) => {
      try {
        editorRef.current?.setContent(content);
        // Update cache immediately for responsive UI
        contentCacheRef.current = content;
        setLastContentUpdate(Date.now());
      } catch (e) {
        console.error('Set content error:', e);
      }
    };

    const stableAppendContent = (content: string) => {
      try {
        // Get current content directly from editor to avoid dependency issues
        const current = editorRef.current?.getContent() || '';
        const newContent = current + content;
        editorRef.current?.setContent(newContent);
        // Update cache immediately for responsive UI
        contentCacheRef.current = newContent;
        setLastContentUpdate(Date.now());
      } catch (e) {
        console.error('Append content error:', e);
      }
    };

    const stablePrependContent = (content: string) => {
      try {
        // Get current content directly from editor to avoid dependency issues
        const current = editorRef.current?.getContent() || '';
        const newContent = content + current;
        editorRef.current?.setContent(newContent);
        // Update cache immediately for responsive UI
        contentCacheRef.current = newContent;
        setLastContentUpdate(Date.now());
      } catch (e) {
        console.error('Prepend content error:', e);
      }
    };

    const stableClearContent = () => {
      try {
        editorRef.current?.setContent('');
        // Update cache immediately for responsive UI
        contentCacheRef.current = '';
        setLastContentUpdate(Date.now());
      } catch (e) {
        console.error('Clear content error:', e);
      }
    };

    // Create a stable getContent function that doesn't depend on external state
    const stableGetContent = () => {
      try {
        const now = Date.now();
        // Return cached content if less than 500ms old
        if (now - lastContentUpdate < 500 && contentCacheRef.current) {
          return contentCacheRef.current;
        }

        const content = editorRef.current?.getContent() || '';
        contentCacheRef.current = content;
        setLastContentUpdate(now);
        return content;
      } catch (e) {
        console.error('Get content error:', e);
        return contentCacheRef.current || '';
      }
    };

    setEditorContext({
      ref: editorRef,
      isConnected: true,
      contentWidth: width,
      contentHeight: height,
      insertContent: stableInsertContent,
      setContent: stableSetContent,
      getContent: stableGetContent, // Use stable version to prevent re-renders
      appendContent: stableAppendContent,
      prependContent: stablePrependContent,
      clearContent: stableClearContent
    });

    // Set up content monitoring for AI context updates - only create interval once
    const interval = setInterval(() => {
      // Use stable content retrieval to avoid dependency issues
      try {
        const content = editorRef.current?.getContent() || '';
        if (content !== contentCacheRef.current) {
          contentCacheRef.current = content;
          setLastContentUpdate(Date.now());
          setCachedContent(content);
        }
      } catch (e) {
        console.error('Content monitoring error:', e);
      }
    }, 1000); // Check content every second

    return () => {
      setEditorContext(null);
      clearInterval(interval);
      if (contentUpdateTimeoutRef.current) {
        clearTimeout(contentUpdateTimeoutRef.current);
      }
    };
  }, [setEditorContext, width, height]); // Removed problematic dependencies



  const handleBeforeRemove = (e) => {
    if (!canGoBack) {
      e.preventDefault();
      console.warn('preventing page change');
      if (Platform.OS === 'web') {
        //Create alert for web
        if (window.confirm('You have unsaved changes. Are you sure to discard them and leave the screen?')) {
          setEditorContext(null); // Clear editor context before navigation
          navigation.dispatch(e.data.action);
          dispatch({ type: HIDE_TOOLBAR });
        }
      } else {
        Alert.alert(
          'Discard changes?',
          'You have unsaved changes. Are you sure to discard them and leave the screen?',
          [
            { text: "Don't leave", style: 'cancel', onPress: () => { } },
            {
              text: 'Discard',
              style: 'destructive',
              onPress: () => {
                setEditorContext(null); // Clear editor context before navigation
                navigation.dispatch(e.data.action);
                dispatch({ type: HIDE_TOOLBAR });
              },
            },
          ]
        );
      }
    } else {
      // User is allowed to go back - clear editor context before leaving
      setEditorContext(null);
    }
  };


  const webPreview = useCallback(async () => {
    if (editorRef.current) {
      setLoading(true);
      await compileEmail().then((res) => setPreviewState(res));
      setLoading(false);
    }
  }, [editorState]);


  const compileEmail = useCallback(async () => {
    // Set the loading state to true.
    try {
      // Import the HTML template using a correct path.
      const htmlTemplate = emailBody; // Use .default to access the string content

      // Compile the HTML template using Handlebars.
      const template = Handlebars.compile(htmlTemplate);

      // Render the compiled template with the editorState.
      const returnHtml = template({ body: editorState });

      // Set the loading state to false when the operation is complete.
      return returnHtml;
    } catch (e) {
      // Handle any errors that occur during compilation.
      console.error(e);
      if (__DEV__) {
        alert(e);
      }
      // Set the loading state to false in the error case as well.
    }
  }, [editorState, previewState, flip]);

  const _imageAssetComponent = ({ item }) => {
    return (
      <Pressable style={{ backgroundColor: 'white', borderRadius: 20, width: 100, height: 100, justifyContent: 'center' }} onPress={() => {
        // Note: insertImage is not available in DomEditorHandle interface
        // You may need to extend the interface or handle image insertion differently
        console.log('Image insertion requested for:', item?.uri);
      }} >
        <Image source={{ uri: item?.uri }} />
      </Pressable>
    )
  }

  useEffect(
    () => {
      // Note: The DomEditor component handles its own initialization
      // No need to set toolbar context for web-based Quill editor
      
      const beforeRemoveListener = navigation.addListener('beforeRemove', handleBeforeRemove);

      return () => {
        beforeRemoveListener();
        setPreviewState(undefined);
        setEditorState(null);
        // Clear editor context when component unmounts to notify AI help
        setEditorContext(null);
      }
    },
    [navigation, setEditorContext]
  );

  const _handleRequestAI = async (data: {
    prompt: string;
    url?: string;
  }) => {
    await apis.genKnowledge(data).then((res => {
      if (editorRef.current) {
        editorRef.current.setContent(res.data.content);
      }
    }))
  }


  const _saveContent = async (latestKnowledgeArticle?: string) => {
    // Get content from editor if not provided
    const content = latestKnowledgeArticle || editorRef.current?.getContent();
    
    if (!content || content.toString().trim() === '') {
      Alert.alert('Error', 'Please write some content before saving.');
      return;
    }
    
    const knowledgeArticleId = (params as any)?.knowledgeArticleId;
    if (!knowledgeArticleId) {
      Alert.alert('Error', 'No article ID found.');
      return;
    }
    
    await apis.saveKnowledgeContent(knowledgeArticleId, content);
  }

  const _publishArticle = async () => {
    const knowledgeArticleId = (params as any)?.knowledgeArticleId;
    if (!knowledgeArticleId) return;
    
    await apis.publishKnowledgeArticle(knowledgeArticleId)
  }

  return (
    <View style={{ 
      flex: 1, 
      backgroundColor: theme ? '#f8f9fa' : '#1a1a1a' 
    }}>
      <DefaultScreen 
        containerStyle={{ 
          left: -3,
          backgroundColor: theme ? '#ffffff' : '#2d2d2d',
        }} 
        scrollDisabled 
        backButton={true}
      >
        <XStack flex={1}>
          {/* Theme-aware editor header */}
          <View style={{
            position: 'absolute',
            top: 10,
            right: 20,
            zIndex: 10,
            backgroundColor: editorColors.surface,
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 20,
            borderWidth: 1,
            borderColor: editorColors.border,
            ...Platform.select({
              web: {
                boxShadow: editorColors.shadow,
              },
              default: {
                shadowColor: theme ? '#000' : '#fff',
                shadowOffset: { width: 0, height: 1 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 2,
              }
            })
          }}>
            <Text style={{
              color: editorColors.text,
              fontSize: 12,
              fontWeight: '500',
            }}>
              {theme ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </Text>
          </View>
          
          <View style={{ 
            alignSelf: 'stretch', 
            flex: 5, 
            minHeight: "100%",
            backgroundColor: editorColors.background,
            borderRadius: 12,
            padding: 4,
            ...Platform.select({
              web: {
                boxShadow: editorColors.shadow,
              },
              default: {
                shadowColor: theme ? '#000' : '#fff',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: theme ? 0.1 : 0.3,
                shadowRadius: 8,
                elevation: 4,
              }
            })
          }}>
            <DomEditor 
              ref={editorRef} 
              saveFn={_saveContent} 
              backFn={() => { }} 
              initialContent={knowledgeContent}
              editorColors={editorColors}
            />
        </View>
        </XStack>
      </DefaultScreen >
      <UploadHtml ref={uploadHtmlRef} callbackFunction={_handleUploadText} />
      {
        showHelp &&
        <View style={{ position: 'absolute', flex: 1, justifyContent: 'flex-end', alignSelf: 'flex-end', width: Platform.OS != 'web' ? '70%' : undefined, left: 50, height: 300, bottom: -20 }}>
          <SwipeableRobot textHelp={['Click on the AI button to generate a prompt for your knowledge article', 'Click on the help button to get help with the editor', 'Click on the upload button to upload your html file']} dismissCallback={() => setShowHelp(false)} />
        </View>
      }
    </View >
  )
}
export default CreateKnowledgeArticle;